import { useState } from "react";
import { ArrowLeft, Phone, MessageCircle, AlertTriangle, Clock, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { telLink, waLink } from "../../lib/api";

export const ExpertPanel = ({ onBack, config, emergency = false, sessionId }) => {
  const hotlines = config?.hotlines || [];
  const isOpen = config?.is_open;
  const bh = config?.business_hours || {};
  const [form, setForm] = useState({ name: "", phone: "", preferred_time: "", notes: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const submitCallback = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) return;
    setSending(true);
    try {
      await api.post("/assistant/callback", { ...form, session_id: sessionId, service: "Callback" });
      setDone(true);
      toast.success("Callback requested — our team will reach out shortly.");
    } catch {
      toast.error("Could not submit. Please call us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="p-4" data-testid={emergency ? "assistant-emergency" : "assistant-expert"}>
      <button onClick={onBack} className="mb-3 inline-flex items-center gap-1.5 text-xs font-bold text-navy hover:text-brandred" data-testid="expert-back">
        <ArrowLeft size={14} /> Back to chat
      </button>

      {emergency ? (
        <div className="mb-3 flex items-center gap-2 rounded-xl bg-brandred/10 p-3">
          <AlertTriangle size={20} className="text-brandred" />
          <div>
            <h3 className="font-display text-base font-black text-brandred">Emergency Assistance</h3>
            <p className="text-xs text-navy">Call our dispatch line now — we'll send help fast.</p>
          </div>
        </div>
      ) : (
        <h3 className="mb-1 font-display text-base font-black text-navy">Speak to an Expert</h3>
      )}

      <div className={`mb-3 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold ${isOpen ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
        <Clock size={12} /> {isOpen ? "Agents online now" : `Offline • ${bh.days || "Mon–Fri"} ${bh.open}:00–${bh.close}:00 ${bh.tz || "GMT"}`}
      </div>

      <div className="space-y-2">
        {hotlines.map((n, i) => (
          <div key={i} className="rounded-xl border border-gray-100 bg-gray-50 p-3" data-testid={`expert-number-${i + 1}`}>
            <p className="text-sm font-bold text-navy">{n}</p>
            <div className="mt-2 flex gap-2">
              <a href={telLink(n)} data-testid={`expert-call-${i + 1}`}
                className={`inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-bold text-white active:scale-95 ${emergency ? "bg-brandred hover:bg-brandred-700 animate-pulse" : "bg-navy hover:bg-navy-700"}`}>
                <Phone size={14} /> Call Now
              </a>
              <a href={waLink(n)} target="_blank" rel="noreferrer" data-testid={`expert-whatsapp-${i + 1}`}
                className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-[#25D366] px-3 py-2 text-xs font-bold text-white hover:brightness-95 active:scale-95">
                <MessageCircle size={14} /> WhatsApp
              </a>
            </div>
          </div>
        ))}
      </div>

      {!emergency && (
        <div className="mt-4 border-t border-gray-100 pt-4">
          <p className="mb-2 text-xs font-bold text-navy">{isOpen ? "Prefer a callback instead?" : "Request a callback"}</p>
          {done ? (
            <div className="flex items-center gap-2 rounded-xl bg-green-50 p-3 text-sm font-semibold text-green-700" data-testid="callback-success">
              <CheckCircle2 size={18} /> We'll call you back soon!
            </div>
          ) : (
            <form onSubmit={submitCallback} className="space-y-2">
              <input required placeholder="Your name" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="callback-name"
                className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-navy" />
              <input required placeholder="Phone number" value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="callback-phone"
                className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-navy" />
              <input placeholder="Preferred time (optional)" value={form.preferred_time}
                onChange={(e) => setForm({ ...form, preferred_time: e.target.value })} data-testid="callback-time"
                className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-navy" />
              <button type="submit" disabled={sending} data-testid="callback-submit"
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-navy px-4 py-2.5 text-sm font-bold text-white hover:bg-navy-700 disabled:opacity-60">
                {sending ? <Loader2 size={16} className="animate-spin" /> : null} Request callback
              </button>
            </form>
          )}
        </div>
      )}
    </div>
  );
};
